import { FadeIn, Section } from "../components/ui";
import { CheckCircle2 } from "lucide-react";
import { Seo } from "../components/Seo";

export function Sobre() {
  return (
    <main className="relative z-10 pt-32 pb-24">
      <Seo
        title="Sobre a P2A | Consultoria de Marketing e Branding"
        description="Conheça a história da P2A Marketing e Branding, fundada por Priscila e Anderson Boaventura, e a missão por trás da nossa consultoria estratégica."
        path="/sobre"
      />
      <h1 className="sr-only">Sobre a P2A Marketing &amp; Branding</h1>
      {/* Section 1: The P2A Story */}
      <Section className="py-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <div className="bg-brand-light/5 border border-brand-light/10 rounded-[2rem] p-8 md:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-brand-accent/5 mix-blend-overlay"></div>
              <div className="relative z-10">
                <div className="text-center mb-10">
                  <h2 className="text-4xl font-black uppercase tracking-tighter mb-2">P2A</h2>
                  <p className="text-brand-accent font-bold tracking-widest text-sm uppercase">Marketing e Branding</p>
                  <p className="text-brand-light/70 text-sm mt-4">Transformamos marketing em estratégia, branding em crescimento e clientes em resultados.</p>
                </div>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="bg-brand-dark/50 p-6 rounded-2xl border border-brand-light/5 text-center">
                    <h3 className="bg-brand-accent text-brand-dark font-bold py-1 px-4 rounded-full inline-block text-xs uppercase tracking-wider mb-4">Missão</h3>
                    <p className="text-xs text-brand-light/80 leading-relaxed">Simplificar o marketing para que marcas alcancem crescimento real, com estratégias personalizadas que unem branding, posicionamento e conversão.</p>
                  </div>
                  <div className="bg-brand-dark/50 p-6 rounded-2xl border border-brand-light/5 text-center">
                    <h3 className="bg-brand-light text-brand-dark font-bold py-1 px-4 rounded-full inline-block text-xs uppercase tracking-wider mb-4">Visão</h3>
                    <p className="text-xs text-brand-light/80 leading-relaxed">Ser a principal referência em marketing estratégico para marcas que buscam crescer com inovação e resultados reais.</p>
                  </div>
                </div>

                <div className="bg-brand-dark/50 p-6 rounded-2xl border border-brand-light/5 text-center mb-8">
                  <h3 className="bg-brand-accent text-brand-dark font-bold py-1 px-8 rounded-full inline-block text-xs uppercase tracking-wider mb-6">Valores</h3>
                  <div className="grid grid-cols-2 gap-4 text-xs text-brand-light/80 text-left">
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-accent shrink-0" /> Simplificação com Estratégia</div>
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-accent shrink-0" /> Transparência e Confiança</div>
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-accent shrink-0" /> Parceria de Longo Prazo</div>
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-accent shrink-0" /> Inovação Contínua</div>
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-brand-accent font-bold text-sm uppercase tracking-wider mb-2">Nosso Propósito</h3>
                  <p className="text-xs text-brand-light/70">Ajudar marcas e empresas a prosperarem com estratégias de marketing adaptadas às suas necessidades, garantindo crescimento sustentável.</p>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="space-y-8">
              <p className="text-xl md:text-2xl text-brand-light/90 leading-relaxed font-medium">
                A P2A nasceu da necessidade real de empresários que não encontravam estratégias de marketing que realmente funcionassem para seus negócios. Criamos soluções personalizadas e eficazes para que marcas e empresas alcancem crescimento sustentável, com marketing que realmente gera resultados.
              </p>
              
              <ul className="space-y-6 text-lg text-brand-light/70">
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-brand-accent shrink-0 mt-2.5"></div>
                  <p>Fundada por Priscila Boaventura e Anderson Boaventura, empresários que entenderam, na prática, os desafios de um negócio físico e digital.</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-brand-accent shrink-0 mt-2.5"></div>
                  <p>Mais do que uma agência, somos parceiros estratégicos.</p>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-brand-accent shrink-0 mt-2.5"></div>
                  <p>Oferecemos soluções flexíveis, inovadoras e orientadas para resultados.</p>
                </li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Section 2: Como podemos ajudar */}
      <Section className="py-24 border-y border-brand-light/5 bg-brand-light/5">
        <FadeIn>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
              Como podemos ajudar a sua <span className="font-highlight italic text-brand-accent lowercase font-normal">empresa?</span>
            </h2>
            <p className="text-xl md:text-2xl font-bold text-brand-light/90 mb-12">
              Está na hora de transformar sua marca em referência no mercado!
            </p>
            
            <div className="flex flex-col items-center gap-6 mb-16 text-lg md:text-xl text-brand-light/80 text-left">
              <div className="flex items-center gap-4 w-full max-w-2xl">
                <CheckCircle2 className="w-8 h-8 text-brand-accent shrink-0" />
                <p>Quer uma estratégia que realmente funcione?</p>
              </div>
              <div className="flex items-center gap-4 w-full max-w-2xl">
                <CheckCircle2 className="w-8 h-8 text-brand-accent shrink-0" />
                <p>Precisa de um time especialista em marketing estratégico?</p>
              </div>
              <div className="flex items-center gap-4 w-full max-w-2xl">
                <CheckCircle2 className="w-8 h-8 text-brand-accent shrink-0" />
                <p>Busca resultados reais e crescimento sustentável?</p>
              </div>
            </div>

            <p className="text-xl font-bold text-brand-light mb-10">
              Fale conosco e descubra como podemos acelerar o crescimento da sua marca!
            </p>
            
            <a 
              href="https://wa.me/5524993144981?text=Vim%20do%20site%20gostaria%20de%20saber%20sobre%20os%20servi%C3%A7os" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-brand-dark bg-brand-accent rounded-full hover:bg-brand-light transition-colors shadow-[0_0_30px_rgba(242,125,38,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)]"
            >
              Falar com um Especialista
            </a>
          </div>
        </FadeIn>
      </Section>

      {/* Section 3: Quem está a frente */}
      <Section className="py-24">
        <FadeIn>
          <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-20 text-center">
            Quem está a frente da <span className="font-highlight italic text-brand-accent lowercase font-normal">P2A?</span>
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-16 max-w-5xl mx-auto">
          {/* Priscila */}
          <FadeIn delay={0.1}>
            <div className="flex flex-col gap-8">
              <div className="aspect-[3/4] rounded-[2rem] overflow-hidden border border-brand-light/10 relative group">
                <div className="absolute inset-0 bg-brand-accent/20 mix-blend-overlay z-10 group-hover:opacity-0 transition-opacity duration-500"></div>
                <img
                  src="https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,h=563,fit=crop/mePxL7e2EOsb0a8L/20250224_ensaio_estudio_0609-mxB4KyxwKNU3N6Nl.jpg"
                  alt="Priscila Boaventura"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-2 font-display">Priscila Boaventura</h3>
                <p className="text-brand-accent font-bold uppercase tracking-widest text-sm mb-6">Gestora de Marketing e Estrategista</p>
                <ul className="space-y-4 text-brand-light/70">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Especialista em Branding e Posicionamento</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Especialista em marketing de franquias</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Coprodutora e estrategista digital, mentorada pela Priscila Zillo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Líder de reposicionamento de marcas e lançamentos de sucesso</span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>

          {/* Anderson */}
          <FadeIn delay={0.2}>
            <div className="flex flex-col gap-8">
              <div className="aspect-[3/4] rounded-[2rem] overflow-hidden border border-brand-light/10 relative group">
                <div className="absolute inset-0 bg-brand-accent/20 mix-blend-overlay z-10 group-hover:opacity-0 transition-opacity duration-500"></div>
                <img
                  src="https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=375,h=563,fit=crop/mePxL7e2EOsb0a8L/pri-53-AMq1gjxj1ot6KxKw.jpg"
                  alt="Anderson Boaventura"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-2 font-display">Anderson Boaventura</h3>
                <p className="text-brand-accent font-bold uppercase tracking-widest text-sm mb-6">Head comercial e especialista em funis de vendas</p>
                <ul className="space-y-4 text-brand-light/70">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Mais de 20 anos em experiência no varejo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Especialista em treinamento de times comerciais</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-light shrink-0 mt-0.5" />
                    <span>Estratégias de crescimento e relacionamento B2B</span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Section 4: Testimonials */}
      <Section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-brand-dark/80 mix-blend-multiply z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426&auto=format&fit=crop"
            alt="Background"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover grayscale opacity-30"
          />
        </div>
        
        <div className="relative z-20 grid md:grid-cols-2 gap-16 max-w-5xl mx-auto text-center">
          <FadeIn delay={0.1}>
            <div className="flex flex-col items-center">
              <div className="flex gap-1 mb-6 text-brand-accent">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                ))}
              </div>
              <p className="text-xl md:text-2xl font-medium text-brand-light/90 mb-8 leading-relaxed">
                "A P2A Marketing transformou nosso negócio com estratégias eficazes e suporte excepcional. Altamente recomendados!"
              </p>
              <p className="font-bold text-brand-light uppercase tracking-widest text-sm">Paulo Costa</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="flex flex-col items-center">
              <div className="flex gap-1 mb-6 text-brand-accent">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                ))}
              </div>
              <p className="text-xl md:text-2xl font-medium text-brand-light/90 mb-8 leading-relaxed">
                "Trabalhar com a P2A foi uma experiência incrível! Eles realmente entendem as necessidades do nosso negócio."
              </p>
              <p className="font-bold text-brand-light uppercase tracking-widest text-sm">Debora Nascimento</p>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
